import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const MichiganRequirementsPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                State Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Michigan <span className="text-blue-600">Cybersecurity Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Understanding Michigan's Insurance Data Security Law and its requirements for financial institutions and insurance entities operating in the state.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only. State cybersecurity regulations are complex and subject to change. We recommend reaching out to our team to confirm you have covered all applicable requirements for Michigan operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Overview */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                  <p className="text-gray-700 text-lg">
                    Michigan has adopted an <strong>Insurance Data Security Law</strong> (added as Chapter 5A to the Insurance Code, MCL 500.550–500.565) that imposes cybersecurity requirements on licensed insurers, producers, and other "licensees" of the Department of Insurance and Financial Services (DIFS). These rules are modeled on the NAIC Insurance Data Security Model Law and require a formal, documented information security program based on risk.
                  </p>
                </div>

                {/* Who is Covered */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Who is Covered?</h2>
                  <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-blue-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Insurance carriers and producers licensed in Michigan</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-blue-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Certain other entities licensed or registered under the Michigan Insurance Code ("licensees")</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-blue-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Some small licensees have limited exemptions from certain obligations, but are still expected to protect nonpublic information</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Core Requirements */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Cybersecurity Program Expectations</h2>
                  <p className="text-gray-700 text-lg mb-6">
                    Michigan's Insurance Data Security Law expects covered licensees to:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-file-shield-line text-blue-600"></i>
                        Maintain a Written Information Security Program (ISP)
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Designed to protect the security and confidentiality of "nonpublic information"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Appropriate to the licensee's size, complexity, and the sensitivity of information handled</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-search-eye-line text-blue-600"></i>
                        Perform Periodic Risk Assessments
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Identify reasonably foreseeable internal and external threats</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Assess the likelihood and potential damage of those threats</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Evaluate the sufficiency of existing security controls</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-shield-check-line text-blue-600"></i>
                        Implement Administrative, Technical, and Physical Safeguards
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Access controls and authentication</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Secure development and change management practices</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Encryption or other compensating controls for data at rest and in transit where appropriate</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Multi-factor authentication where reasonable</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Secure disposal of nonpublic information in any format</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Monitoring systems for unauthorized access or tampering</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-team-line text-blue-600"></i>
                        Vendor / Third-Party Oversight
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Exercise due diligence in selecting service providers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Require providers by contract to maintain appropriate security safeguards</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Monitor providers' compliance as part of your risk management process</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-alarm-warning-line text-blue-600"></i>
                        Incident Response Planning
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Maintain a written incident response plan that describes roles, decision making, communication, investigation, containment, recovery, and documentation steps for cybersecurity events</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-settings-3-line text-blue-600"></i>
                        Ongoing Governance
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Periodic updates to the ISP as threats, vulnerabilities, business operations, and technology change</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>Cybersecurity risks integrated into enterprise risk management</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                          <span>For certain licensees, board or senior management oversight and periodic reporting on the information security program</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Breach Notification */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Breach Notification and Regulator Reporting</h2>
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Covered licensees must investigate cybersecurity events involving nonpublic information</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>If certain thresholds are met (e.g., impact on Michigan residents or the licensee's overall operations), notification to DIFS and affected consumers is required within specified time frames and with prescribed content</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Additional federal or other state breach notice requirements may also apply depending on the nature of the data and where consumers reside</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need Help with Michigan Compliance?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our team helps financial institutions understand and implement the controls needed to meet Michigan's Insurance Data Security Law requirements.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/schedule-call" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md whitespace-nowrap cursor-pointer">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Consultation
                  </button>
                </a>
                <a href="/risk-assessment" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-blue-600 whitespace-nowrap cursor-pointer">
                    <i className="ri-shield-check-line mr-2"></i>
                    Get Compliance Assessment
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MichiganRequirementsPage;
